import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import Button from '@decisiv/ui-components/lib/components/Button';
import Share from '@decisiv/iconix/lib/components/Share';
import InfoCircle from '@decisiv/iconix/lib/components/InfoCircle';
import Check from '@decisiv/iconix/lib/components/Check';
import Minus from '@decisiv/iconix/lib/components/Minus';
import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import ArrowUp from '@decisiv/iconix/lib/components/ArrowUp';
import Trash from '@decisiv/iconix/lib/components/Trash';
import Lock from '@decisiv/iconix/lib/components/Lock';
import LockOpen from '@decisiv/iconix/lib/components/LockOpen';
import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';
import ExclamationCircleF from '@decisiv/iconix/lib/components/ExclamationCircleF';
import Upload from '@decisiv/iconix/lib/components/Upload';
import { currentVersion, safeParse } from './atUtils';

// ---- Migrate wizard ------------------------------------------------------

export function MigrateModal({
  action: _action,
  fromVersion,
  toVersion,
  integrationsOnFrom,
  onClose,
  onMigrate,
  onCompare = null,
}) {
  const [selected, setSelected] = useState(() => new Set(integrationsOnFrom));
  const [submitting, setSubmitting] = useState(false);

  const toggle = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };

  const allChecked =
    selected.size === integrationsOnFrom.length &&
    integrationsOnFrom.length > 0;
  const someChecked = selected.size > 0 && !allChecked;
  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(integrationsOnFrom));
  };

  let allCheckClass = '';
  if (allChecked) allCheckClass = 'checked';
  else if (someChecked) allCheckClass = 'indeterminate';

  async function handleMigrate() {
    setSubmitting(true);
    await onMigrate([...selected]);
    setSubmitting(false);
  }

  return (
    <Modal
      visible
      onClose={onClose}
      icon={ArrowUp}
      intent="information"
      title={`Migrate integrations to v${toVersion.version}`}
      actions={[
        {
          id: 'skip',
          text: 'Skip for now',
          kind: 'secondary',
          onClick: onClose,
        },
        {
          id: 'migrate',
          text: `Migrate ${selected.size}`,
          icon: ArrowRight,
          onClick: handleMigrate,
          disabled: selected.size === 0 || submitting,
        },
      ]}
    >
      <div className="at-portal">
        <p className="muted" style={{ marginTop: 0 }}>
          <strong style={{ color: 'var(--if-ink-1)' }}>
            {integrationsOnFrom.length} integration
            {integrationsOnFrom.length === 1 ? '' : 's'}
          </strong>{' '}
          currently on v{fromVersion.version}. Select which to repoint at v
          {toVersion.version}. Existing assignments stay on v
          {fromVersion.version} until they&apos;re migrated here.
        </p>
        {onCompare && (
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              className="at-compare-link-btn"
              onClick={onCompare}
            >
              <Share size="small" /> See what&apos;s changing between v
              {fromVersion.version} and v{toVersion.version}
            </button>
          </div>
        )}
        {integrationsOnFrom.length === 0 ? (
          <div className="empty-state" style={{ marginTop: 14 }}>
            <div className="empty-icon">
              <InfoCircle size="medium" />
            </div>
            <div className="empty-title">
              No integrations on v{fromVersion.version}
            </div>
            Nothing to migrate.
          </div>
        ) : (
          <div className="at-mig-list" style={{ marginTop: 12 }}>
            <div
              className="at-mig-row row-all"
              onClick={toggleAll}
              role="presentation"
            >
              <span
                className={`check ${allCheckClass}`}
                aria-checked={allChecked}
                role="checkbox"
                tabIndex={0}
              >
                {allChecked && <Check size="small" />}
                {someChecked && <Minus size="small" />}
              </span>
              <div className="row-info">
                <div className="row-name">
                  All ({integrationsOnFrom.length})
                </div>
              </div>
              <span className="muted small">{selected.size} selected</span>
            </div>
            {integrationsOnFrom.map((i) => {
              const checked = selected.has(i);
              return (
                <div
                  className="at-mig-row"
                  key={i}
                  onClick={() => toggle(i)}
                  role="presentation"
                >
                  <span
                    className={`check ${checked ? 'checked' : ''}`}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                  >
                    {checked && <Check size="small" />}
                  </span>
                  <div className="row-info">
                    <div className="row-name">{i}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="muted small" style={{ marginTop: 12, marginBottom: 0 }}>
          {integrationsOnFrom.length - selected.size > 0
            ? `${integrationsOnFrom.length - selected.size} will stay on v${
                fromVersion.version
              }`
            : 'All selected will move'}
        </p>
      </div>
    </Modal>
  );
}
MigrateModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  fromVersion: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  toVersion: PropTypes.object.isRequired,
  integrationsOnFrom: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onMigrate: PropTypes.func.isRequired,
  onCompare: PropTypes.func,
};

// ---- Delete modal --------------------------------------------------------

export function DeleteModal({
  action,
  version,
  onClose,
  onDelete,
  onMigrateInstead,
}) {
  const cur = currentVersion(action);
  const newerExists = !version.isCurrent;
  const inUse = version.integrations.length > 0;

  const confirmAction =
    inUse && newerExists
      ? {
          id: 'migrate-first',
          text: `Migrate to v${cur.version} first`,
          icon: ArrowUp,
          onClick: () => onMigrateInstead(version.version),
        }
      : {
          id: 'delete',
          text: newerExists ? `Delete v${version.version}` : 'Delete action',
          icon: Trash,
          intent: 'danger',
          disabled: inUse,
          onClick: () => onDelete(version.version),
        };

  return (
    <Modal
      visible
      onClose={onClose}
      icon={Trash}
      intent="danger"
      title={
        newerExists ? `Delete v${version.version}?` : `Delete ${action.name}?`
      }
      actions={[
        { id: 'cancel', text: 'Cancel', kind: 'secondary', onClick: onClose },
        confirmAction,
      ]}
    >
      <div className="at-portal">
        {!inUse && !newerExists && (
          <>
            <p>
              This will permanently remove{' '}
              <code className="mono">{action.name}</code> and its full version
              history. This action is not assigned to any integration.
            </p>
            <p className="muted small">This cannot be undone.</p>
          </>
        )}
        {!inUse && newerExists && (
          <p>
            This will remove v{version.version} from the version chain of{' '}
            <code className="mono">{action.name}</code>. Newer versions and
            assignments remain intact.
          </p>
        )}
        {inUse && !newerExists && (
          <>
            <p>
              <code className="mono">{action.name}</code> is still in use by{' '}
              <strong>
                {version.integrations.length} integration
                {version.integrations.length === 1 ? '' : 's'}
              </strong>
              . You can&apos;t delete an action that&apos;s currently assigned.
            </p>
            <div className="at-warn-list">
              <strong>Assigned to:</strong>
              <ul>
                {version.integrations.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
            <p className="at-hint" style={{ marginTop: 10 }}>
              Unassign from the integration builder, or migrate to a newer
              version first.
            </p>
          </>
        )}
        {inUse && newerExists && (
          <>
            <p>
              v{version.version} of <code className="mono">{action.name}</code>{' '}
              still has{' '}
              <strong>
                {version.integrations.length} integration
                {version.integrations.length === 1 ? '' : 's'}
              </strong>{' '}
              pointing at it. Migrate them to v{cur.version} first, then this
              version can be removed.
            </p>
            <div className="at-warn-list">
              <strong>Still on v{version.version}:</strong>
              <ul>
                {version.integrations.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

DeleteModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMigrateInstead: PropTypes.func.isRequired,
};

// ---- Lock confirmation ---------------------------------------------------

export function LockModal({ action, version, onClose, onConfirm }) {
  const willLock = !version.locked;
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  let confirmLabel;
  if (busy) confirmLabel = willLock ? 'Locking…' : 'Unlocking…';
  else confirmLabel = willLock ? 'Lock version' : 'Unlock version';

  return (
    <Modal
      visible
      onClose={onClose}
      icon={willLock ? Lock : LockOpen}
      intent={willLock ? 'warning' : 'information'}
      title={`${willLock ? 'Lock' : 'Unlock'} v${version.version}?`}
      actions={[
        {
          id: 'cancel',
          text: 'Cancel',
          kind: 'secondary',
          onClick: onClose,
          disabled: busy,
        },
        {
          id: 'confirm',
          text: confirmLabel,
          icon: willLock ? Lock : LockOpen,
          onClick: handleConfirm,
          disabled: busy,
        },
      ]}
    >
      <div className="at-portal">
        {willLock ? (
          <>
            <p>
              Locking v{version.version} of{' '}
              <code className="mono">{action.name}</code> hides it from the
              integration-builder picker. Existing assignments stay on this
              version, but no new integrations can pick it up.
            </p>
            <p className="at-hint">
              Use this to retire old versions gracefully without breaking
              existing wiring.
            </p>
          </>
        ) : (
          <p>
            Unlocking v{version.version} makes it pickable again from the
            integration builder. Use sparingly — if a newer version exists,
            prefer new integrations to use the latest.
          </p>
        )}
      </div>
    </Modal>
  );
}

LockModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

// ---- Import modal --------------------------------------------------------

export function ImportModal({ onClose, onImport }) {
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const readFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(file);
  };

  const handleBrowseFile = () => fileInputRef.current?.click();

  async function handleImport() {
    setError(null);
    const parsed = safeParse(text);
    if (!parsed.ok) {
      setError('That is not valid JSON. Paste an exported action document.');
      return;
    }

    setSubmitting(true);
    const res = await onImport(parsed.data);
    setSubmitting(false);

    if (res && res.errors) {
      const first = res.errors[0] || {};
      const pointer = first.source && first.source.pointer;
      setError(
        [first.detail || 'Import failed.', pointer ? `(${pointer})` : null]
          .filter(Boolean)
          .join(' '),
      );
      return;
    }
    onClose();
  }

  return (
    <>
      <Modal
        visible
        onClose={onClose}
        icon={Upload}
        intent="information"
        title="Import action"
        actions={[
          {
            id: 'cancel',
            text: 'Cancel',
            kind: 'secondary',
            onClick: onClose,
            disabled: submitting,
          },
          {
            id: 'import',
            text: submitting ? 'Importing…' : 'Import',
            icon: Upload,
            onClick: handleImport,
            disabled: submitting || text.trim() === '',
          },
        ]}
      >
        <div className="at-portal">
          <p className="muted" style={{ marginTop: 0 }}>
            Paste an exported action document (or upload the .json file). A new
            action is created only if its name doesn&apos;t already exist here —
            an existing name is skipped, never overwritten.
          </p>
          <Button
            text="Select file"
            icon={Upload}
            kind="secondary"
            size="small"
            onClick={handleBrowseFile}
            style={{ marginBottom: 10 }}
          />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              '{\n  "format": "inf.action/v1",\n  "category": { "name": "…" },\n  "action": { "name": "…", … }\n}'
            }
            rows={12}
            spellCheck={false}
            style={{
              width: '100%',
              fontFamily: 'monospace',
              fontSize: 12,
              padding: 8,
              boxSizing: 'border-box',
            }}
          />
          {error && (
            <div className="banner banner-warn" style={{ marginTop: 10 }}>
              <span className="banner-icon">
                <ExclamationTriangle size="medium" />
              </span>
              <div className="banner-body">{error}</div>
            </div>
          )}
        </div>
      </Modal>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={readFile}
        style={{ display: 'none' }}
      />
    </>
  );
}

ImportModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
};

// ---- Overflow menu -------------------------------------------------------

export function OverflowMenu({
  x,
  y,
  version,
  onLock,
  onUnlock,
  onDelete,
  onClose,
}) {
  useEffect(() => {
    const handler = () => onClose();
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const activateOnKey = (cb) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      cb();
    }
  };

  return (
    <div
      className="at-menu"
      style={{ left: x, top: y }}
      onMouseDown={(e) => e.stopPropagation()}
      role="menu"
      tabIndex={-1}
    >
      {version.locked ? (
        <div
          className="at-menu-item"
          onClick={onUnlock}
          onKeyDown={activateOnKey(onUnlock)}
          role="menuitem"
          tabIndex={0}
        >
          <LockOpen size="small" /> Unlock v{version.version}
        </div>
      ) : (
        <div
          className="at-menu-item"
          onClick={onLock}
          onKeyDown={activateOnKey(onLock)}
          role="menuitem"
          tabIndex={0}
        >
          <Lock size="small" /> Lock v{version.version}
        </div>
      )}
      <div className="at-menu-sep" />
      <div
        className="at-menu-item danger"
        onClick={onDelete}
        onKeyDown={activateOnKey(onDelete)}
        role="menuitem"
        tabIndex={0}
      >
        <Trash size="small" /> Delete…
      </div>
    </div>
  );
}

OverflowMenu.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
  onLock: PropTypes.func.isRequired,
  onUnlock: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ---- Toast tray ----------------------------------------------------------

const TOAST_ICON_MAP = {
  success: { Icon: CheckCircleF, color: 'var(--if-success)' },
  warn: { Icon: ExclamationTriangle, color: 'var(--if-warning)' },
  error: { Icon: ExclamationCircleF, color: 'var(--if-danger)' },
};
const TOAST_DEFAULT = { Icon: InfoCircle, color: 'var(--if-accent)' };

export function ToastTray({ toasts }) {
  return (
    <div className="at-toast-tray" aria-live="polite">
      {toasts.map((toast) => {
        const { Icon: ToastIcon, color } =
          TOAST_ICON_MAP[toast.kind] || TOAST_DEFAULT;
        return (
          <div className={`at-toast ${toast.kind || ''}`} key={toast.id}>
            <span className="toast-icon">
              <ToastIcon size="medium" color={color} />
            </span>
            <div className="toast-body">
              <div className="toast-title">{toast.title}</div>
              {toast.msg && <div className="toast-msg">{toast.msg}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

ToastTray.propTypes = {
  toasts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.string,
      title: PropTypes.string.isRequired,
      msg: PropTypes.string,
    }),
  ).isRequired,
};
